<section class="module-permissions">
  <app-page-header
    title="Permisos de Módulos"
    subtitle="Gestiona el acceso a módulos del sistema por usuario y empresa"
    icon="Shield"
  />

  @if (!canManage()) {
    <div class="module-permissions__denied card-like">
      <p class="module-permissions__denied-title">Acceso restringido</p>
      <p class="module-permissions__denied-text">
        Solo el superadministrador puede gestionar permisos de módulos.
      </p>
    </div>
  } @else {
    @if (modulePermissionsApiDown()) {
      <app-vault-page-alert
        type="info"
        title="API no disponible"
        [message]="error() ?? 'No se puede consultar ni crear permisos de módulo hasta que el backend corrija el endpoint.'"
        (closed)="error.set(null)"
      />
    } @else if (error()) {
      <app-vault-page-alert
        type="error"
        title="Error"
        [message]="error()"
        (closed)="error.set(null)"
      />
    }
    @if (success()) {
      <app-vault-page-alert
        type="success"
        title="Listo"
        [message]="success()"
        (closed)="success.set(null)"
      />
    }

    <div class="module-permissions__toolbar">
      <lib-button
        variant="primary"
        type="button"
        prefixIcon="Plus"
        [disabled]="loading() || modulePermissionsApiDown()"
        (clicked)="openCreate()"
      >
        Crear permiso
      </lib-button>
      <lib-button variant="secondary" type="button" prefixIcon="RefreshCcw" [loading]="loading()" (clicked)="reload()">
        Actualizar
      </lib-button>
    </div>

    @if (showPrefillContext()) {
      <p class="module-permissions__prefill-context" role="status">
        Empresa del usuario seleccionado:
        <strong>{{ prefillTenantLabel() || 'Sin empresa asignada' }}</strong>
      </p>
    }

    <div class="module-permissions__filters card-like">
      <h3 class="module-permissions__filters-title">Filtros</h3>
      <div class="module-permissions__filters-row">
        <div class="module-permissions__filters-fields">
          <div class="module-permissions__filter-field">
            <app-field-label label="Módulo" />
            <lib-select
              label=""
              [options]="filterModuleOptions()"
              [value]="filterModule()"
              (valueChange)="onFilterModuleChange($event)"
            />
          </div>
          @if (!singleTenantMode) {
            <div class="module-permissions__filter-field">
              <app-field-label label="Empresa" />
              <lib-select
                label=""
                [options]="filterTenantOptions()"
                [value]="filterTenant()"
                (valueChange)="onFilterTenantChange($event)"
              />
            </div>
          } @else if (showPrefillContext()) {
            <div class="module-permissions__filter-field">
              <app-field-label label="Empresa" />
              <p class="module-permissions__hint module-permissions__readonly-value">
                {{ prefillTenantLabel() || 'Sin empresa asignada' }}
              </p>
            </div>
          }
        </div>
        <div class="module-permissions__filter-actions">
          <lib-button variant="secondary" type="button" (clicked)="applyFilters()">Aplicar filtros</lib-button>
          <lib-button variant="secondary" type="button" (clicked)="clearFilters()">Limpiar</lib-button>
        </div>
      </div>
    </div>

    @if (loading()) {
      <div class="module-permissions__skeleton" aria-busy="true">
        <lib-skeleton variant="rectangle" [height]="'48px'" />
        <lib-skeleton variant="rectangle" [height]="'48px'" />
      </div>
    } @else if (permissions().length === 0) {
      <lib-empty icon="Shield" message="Sin permisos" description="No hay permisos de módulo registrados." />
    } @else {
      <div class="module-permissions__table-wrap">
        <lib-search
          placeholder="Buscar por usuario o empresa..."
          [searchTerm]="searchTerm()"
          (searchTermChange)="onTableSearchChange($event)"
          [loading]="loading()"
        />
        @if (tableData().length === 0) {
          <lib-empty icon="Search" message="Sin resultados" description="No hay permisos que coincidan con la búsqueda." />
        } @else {
          <lib-table
            [config]="tableConfig()"
            [data]="tableData()"
            [loading]="loading()"
            [searchTerm]="searchTerm()"
            (searchTermChange)="onTableSearchChange($event)"
            [pageSize]="tablePageSize()"
            [pageSizeOptions]="tablePageSizeOptions"
            (pageChange)="onTablePageChange($event)"
            [sortColumn]="tableSortColumn()"
            [sortDirection]="tableSortDirection()"
            (sortChange)="onTableSort($event)"
            (action)="onTableAction($event)"
          >
            <ng-template #cellTemplate let-row let-column="column">
              @if (column.key === 'statusLabel') {
                <lib-badge
                  [text]="row.statusLabel"
                  [variant]="row.is_enabled ? 'success' : 'neutral'"
                  size="small"
                />
              }
            </ng-template>
          </lib-table>
        }
      </div>
    }
  }
</section>

@if (createOpen()) {
  <div class="module-permissions__backdrop" (click)="closeCreate()" role="presentation"></div>
  <div class="module-permissions__dialog module-permissions__dialog--lg" role="dialog" aria-modal="true">
    <header class="module-permissions__dialog-head">
      <h2>Crear permiso de módulo</h2>
      <p class="module-permissions__form-intro">
        Asigne acceso a un módulo Vault para el usuario. La empresa se toma de la asignación del usuario.
      </p>
    </header>
    <div class="module-permissions__dialog-body" (click)="$event.stopPropagation()">
      <app-grid-form preset="2cols" [columns]="2" gap="0.75rem">
        <app-grid-form-item [span]="'full'">
          <app-field-label label="Usuario" [required]="true" />
          <lib-select label="" [options]="userOptions()" [value]="formUserId()" (valueChange)="onFormUserIdChange($event)" />
        </app-grid-form-item>
        <app-grid-form-item [span]="1">
          <app-field-label label="Empresa" [required]="true" />
          @if (singleTenantMode) {
            <p class="module-permissions__hint module-permissions__readonly-value">{{ formTenantLabel() }}</p>
          } @else {
            <lib-select label="" [options]="tenantOptions()" [value]="formTenantId()" (valueChange)="onFormTenantChange($event)" />
          }
        </app-grid-form-item>
        <app-grid-form-item [span]="1">
          <app-field-label label="Módulo" [required]="true" />
          <lib-select label="" [options]="moduleSelectOptions()" [value]="formModule()" (valueChange)="onFormModuleChange($event)" />
        </app-grid-form-item>
        <app-grid-form-item [span]="'full'" class="module-permissions__checkbox-field">
          <app-field-label label="Habilitado" />
          <lib-checkbox label="" [checked]="formEnabled()" (checkedChange)="formEnabled.set($event)" />
        </app-grid-form-item>
      </app-grid-form>

      @if (isIngestModule() && formTenantId()) {
        <div class="module-permissions__projects">
          <h3>Expedientes de ingesta (opcional)</h3>
          <p class="module-permissions__hint">Sin selección = acceso a todos los expedientes del tenant.</p>
          @if (loadingProjects()) {
            <p class="module-permissions__muted">Cargando expedientes...</p>
          } @else if (ingestProjects().length === 0) {
            <p class="module-permissions__muted">No hay expedientes o no se pudieron cargar.</p>
          } @else {
            <ul class="module-permissions__project-list">
              @for (proj of ingestProjects(); track proj.id) {
                <li>
                  <lib-checkbox
                    [label]="proj.name"
                    [checked]="isProjectSelected(proj.id)"
                    (checkedChange)="toggleProjectSelection(proj.id, $event)"
                  />
                </li>
              }
            </ul>
          }
        </div>
      }

      <footer class="module-permissions__dialog-footer">
        <lib-button variant="secondary" type="button" [disabled]="saving()" (clicked)="closeCreate()">Cancelar</lib-button>
        <lib-button variant="primary" type="button" [loading]="saving()" (clicked)="saveCreate()">Crear</lib-button>
      </footer>
    </div>
  </div>
}

@if (editOpen() && editingPermission()) {
  <div class="module-permissions__backdrop" (click)="closeEdit()" role="presentation"></div>
  <div class="module-permissions__dialog module-permissions__dialog--sm" role="dialog" aria-modal="true">
    <header class="module-permissions__dialog-head">
      <h2>Editar permiso</h2>
      <p>{{ editingPermission()!.user_full_name || editingPermission()!.user_email }} — {{ editingPermission()!.module_name }}</p>
    </header>
    <div class="module-permissions__dialog-body" (click)="$event.stopPropagation()">
      <app-field-label label="Habilitado" />
      <lib-checkbox
        label=""
        [checked]="editingPermission()!.is_enabled"
        (checkedChange)="onEditEnabledChange($event)"
      />
      <footer class="module-permissions__dialog-footer">
        <lib-button variant="secondary" type="button" [disabled]="saving()" (clicked)="closeEdit()">Cancelar</lib-button>
        <lib-button variant="primary" type="button" [loading]="saving()" (clicked)="saveEdit()">Guardar</lib-button>
      </footer>
    </div>
  </div>
}

@if (deleteOpen()) {
  <div class="module-permissions__backdrop" (click)="closeDelete()" role="presentation"></div>
  <div class="module-permissions__dialog module-permissions__dialog--sm" role="dialog" aria-modal="true">
    <header class="module-permissions__dialog-head">
      <h2>Eliminar permiso</h2>
      <p>¿Eliminar el permiso de {{ permissionToDelete()?.module_name }} para {{ permissionToDelete()?.user_full_name }}?</p>
    </header>
    <footer class="module-permissions__dialog-footer">
      <lib-button variant="secondary" type="button" [disabled]="saving()" (clicked)="closeDelete()">Cancelar</lib-button>
      <lib-button variant="primary" type="button" [loading]="saving()" (clicked)="confirmDelete()">Eliminar</lib-button>
    </footer>
  </div>
}

@if (foldersOpen() && folderPermission()) {
  <div class="module-permissions__backdrop" (click)="closeFolders()" role="presentation"></div>
  <div class="module-permissions__dialog module-permissions__dialog--lg" role="dialog" aria-modal="true">
    <header class="module-permissions__dialog-head">
      <h2>Gestionar carpetas — ingesta</h2>
      <p>{{ folderPermission()!.user_full_name }} · {{ folderPermission()!.tenant_name }}</p>
    </header>
    <div class="module-permissions__dialog-body" (click)="$event.stopPropagation()">
      <h3>Expedientes asignados</h3>
      @if (folderPermission()!.folder_permissions.length === 0) {
        <p class="module-permissions__muted">Sin restricción por expediente (acceso a todos).</p>
      } @else {
        <ul class="module-permissions__folder-list">
          @for (f of folderPermission()!.folder_permissions; track f.id) {
            <li>
              <span>{{ f.project_name || ('Expediente #' + f.project_id) }}</span>
              <lib-button variant="secondary" type="button" (clicked)="openRemoveFolder(f)">Quitar</lib-button>
            </li>
          }
        </ul>
      }

      <div class="module-permissions__add-folder">
        <h3>Agregar expediente</h3>
        <lib-select
          label="Expediente"
          [options]="ingestProjectOptions()"
          [value]="folderProjectId()"
          (valueChange)="onFolderProjectIdChange($event)"
        />
        <lib-button variant="primary" type="button" [loading]="saving()" [disabled]="!folderProjectId()" (clicked)="addFolderPermission()">
          Agregar
        </lib-button>
      </div>

      <footer class="module-permissions__dialog-footer">
        <lib-button variant="secondary" type="button" (clicked)="closeFolders()">Cerrar</lib-button>
      </footer>
    </div>
  </div>
}

@if (removeFolderOpen()) {
  <div class="module-permissions__backdrop" (click)="closeRemoveFolder()" role="presentation"></div>
  <div class="module-permissions__dialog module-permissions__dialog--sm" role="dialog" aria-modal="true">
    <header class="module-permissions__dialog-head">
      <h2>Quitar expediente</h2>
      <p>¿Quitar {{ folderToRemove()?.project_name || folderToRemove()?.project_id }} del permiso?</p>
    </header>
    <footer class="module-permissions__dialog-footer">
      <lib-button variant="secondary" type="button" [disabled]="saving()" (clicked)="closeRemoveFolder()">Cancelar</lib-button>
      <lib-button variant="primary" type="button" [loading]="saving()" (clicked)="confirmRemoveFolder()">Quitar</lib-button>
    </footer>
  </div>
}
